import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useContentSelfHeal } from "@/hooks/useContentSelfHeal";
import { usePageMeta } from "@/hooks/usePageMeta";

import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { DashboardDataProvider } from "@/context/DashboardDataContext";
import { ContentNavigationProvider } from "@/context/ContentNavigationContext";
import { PublicAuthProvider } from "@/context/PublicAuthContext";

import { ResizablePanelGroup, ResizablePanel } from "@/components/ui/resizable";
import { ResizeHandle } from "@/components/ui/resizable/ResizeHandle";
import { supabase } from "@/integrations/supabase/client";

import { ContentItem } from "@/types/content";

import { ContentViewer } from "@/components/content-viewer/ContentViewer";
import { DocumentViewerSkeleton } from "@/components/content-viewer/DocumentViewerSkeleton";
import { ContentShareDialog } from "@/components/content/ContentShareDialog";
import { ContentChatRoot } from "@/components/content-chat";
import { ContentToggleButton } from "@/components/content-viewer/ContentToggleButton";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { PublicAuthOverlay } from "@/components/public/PublicAuthOverlay";

export default function ContentPage() {
  usePageMeta("Content — Shattara AI", "", { noIndex: true });
  const { contentId } = useParams();
  const isMobile = useIsMobile();
  const { user, loading: authLoading } = useAuth();
  const [content, setContent] = useState<ContentItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [showContentViewer, setShowContentViewer] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 768,
  );
  const [isPublicView, setIsPublicView] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Auto-trigger processing for stale content (e.g. imported from Explore before fix)
  useContentSelfHeal(contentId, content?.type);

  useEffect(() => {
    if (authLoading) return;

    async function loadContent() {
      const { data, error } = await supabase
        .from("content")
        .select("*")
        .eq("id", contentId)
        .maybeSingle();

      if (error) console.error(error);
      
      if (data) {
        const mapped: ContentItem = {
          id: data.id,
          title: data.title,
          type: data.type as ContentItem["type"],
          thumbnailUrl: data.thumbnail_url,
          sourceUrl: data.source_url,
          storagePath: data.storage_path,
          roomId: data.room_id,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          visibility: data.visibility as "private" | "public",
          text_extraction_status: data.text_extraction_status,
          transcript_status: data.transcript_status,
          chapters_status: data.chapters_status,
          conversion_status: data.conversion_status,
        };
        setContent(mapped);

        // Determine if this is a public view (not owner and not a team member)
        const isOwner = user && data.user_id === user.id;
        let isTeamMember = false;

        if (!isOwner && user && data.room_id) {
          // Check if the content's room belongs to a team the user is part of
          const { data: room } = await supabase
            .from("rooms")
            .select("team_id")
            .eq("id", data.room_id)
            .maybeSingle();

          if (room?.team_id) {
            const { data: membership } = await supabase
              .from("team_members")
              .select("id")
              .eq("team_id", room.team_id)
              .eq("user_id", user.id)
              .maybeSingle();

            isTeamMember = !!membership;
          }
        }

        setIsPublicView(!isOwner && !isTeamMember);
      } else {
        setContent(null);
        setNotFound(true);
      }
      setLoading(false);
    }

    loadContent();
  }, [contentId, user, authLoading]);

  const handleVisibilityChange = (contentId: string, visibility: "private" | "public") => {
    if (content && content.id === contentId) {
      setContent({ ...content, visibility });
    }
  };

  const handleContentTitleChange = (newTitle: string) => {
    if (content) {
      setContent({ ...content, title: newTitle });
    }
  };

  // Not found state
  if (!loading && !authLoading && notFound) {
    return (
      <DashboardDataProvider>
        <DashboardShell isPublicView={!user}>
          <div className="flex h-[calc(100vh-56px)] flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Content not found</h2>
            <p className="text-muted-foreground">This content doesn't exist or is not publicly available.</p>
          </div>
        </DashboardShell>
      </DashboardDataProvider>
    );
  }

  // Public view — show full layout but all interactions trigger auth overlay
  if (isPublicView && !loading) {
    return (
      <DashboardDataProvider>
        <ContentNavigationProvider>
          <PublicAuthProvider isPublicView>
            <DashboardShell isPublicView>
              <PublicAuthOverlay />

              {/* Desktop Layout */}
              <div className="hidden lg:flex h-[calc(100vh-56px)] w-full bg-background">
                <ResizablePanelGroup direction="horizontal" className="w-full h-full">
                  <ResizablePanel defaultSize={45} minSize={30} maxSize={70}>
                    <div className="h-full w-full p-3 md:p-4">
                      <div className={`h-full w-full overflow-hidden rounded-2xl bg-card ${
                        content && ['youtube', 'video', 'audio', 'recording'].includes(content.type) 
                          ? '' 
                          : 'border border-border'
                      }`}>
                        <div className="relative h-full w-full overflow-hidden rounded-xl bg-background">
                          {content ? (
                            <ContentViewer content={content} />
                          ) : (
                            <DocumentViewerSkeleton />
                          )}
                        </div>
                      </div>
                    </div>
                  </ResizablePanel>

                  <ResizeHandle className="bg-border/60 w-[4px]" />

                  <ResizablePanel defaultSize={55} minSize={30} maxSize={70}>
                    <div className="h-full w-full bg-background overflow-hidden">
                      {content ? (
                        <ContentChatRoot content={content} />
                      ) : (
                        <div className="flex h-full items-center justify-center text-muted-foreground">
                          Chat unavailable
                        </div>
                      )}
                    </div>
                  </ResizablePanel>
                </ResizablePanelGroup>
              </div>

              {/* Mobile/Tablet Layout */}
              <div className="flex lg:hidden flex-col h-[calc(100dvh-56px)] w-full bg-background">
                {showContentViewer && content && (
                  <div className="h-[50dvh] flex-shrink-0 overflow-hidden">
                    <div className="h-full w-full p-2">
                      <div className={`h-full w-full overflow-hidden rounded-xl bg-card ${
                        ['youtube', 'video', 'audio', 'recording'].includes(content.type) 
                          ? '' 
                          : 'border border-border'
                      }`}>
                        <div className="relative h-full w-full overflow-hidden rounded-lg bg-background">
                          <ContentViewer content={content} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {content && (
                  <ContentToggleButton
                    content={content}
                    isExpanded={showContentViewer}
                    onToggle={() => setShowContentViewer(!showContentViewer)}
                  />
                )}

                <div className="flex-1 min-h-0 overflow-hidden">
                  {content ? (
                    <ContentChatRoot content={content} isContentViewerVisible={showContentViewer} />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      Chat unavailable
                    </div>
                  )}
                </div>
              </div>
            </DashboardShell>
          </PublicAuthProvider>
        </ContentNavigationProvider>
      </DashboardDataProvider>
    );
  }

  // Owner view — full experience
  return (
    <DashboardDataProvider>
      <ContentNavigationProvider>
        <PublicAuthProvider isPublicView={false}>
        <DashboardShell 
          chat={content} 
          onShareChat={() => setIsShareDialogOpen(true)}
          onContentTitleChange={handleContentTitleChange}
        >
          {/* Desktop Layout */}
          <div className="hidden lg:flex h-[calc(100vh-56px)] w-full bg-background">
            <ResizablePanelGroup direction="horizontal" className="w-full h-full">
              <ResizablePanel defaultSize={45} minSize={30} maxSize={70}>
                <div className="h-full w-full p-3 md:p-4">
                  <div className={`h-full w-full overflow-hidden rounded-2xl bg-card ${
                    content && ['youtube', 'video', 'audio', 'recording'].includes(content.type) 
                      ? '' 
                      : 'border border-border'
                  }`}>
                    <div className="relative h-full w-full overflow-hidden rounded-xl bg-background">
                      {loading || authLoading ? (
                        <DocumentViewerSkeleton />
                      ) : content ? (
                        <ContentViewer content={content} />
                      ) : (
                        <div className="flex h-full items-center justify-center text-muted-foreground">
                          Content not found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </ResizablePanel>

              <ResizeHandle className="bg-border/60 w-[4px]" />

              <ResizablePanel defaultSize={55} minSize={30} maxSize={70}>
                <div className="h-full w-full bg-background overflow-hidden">
                  {content ? (
                    <ContentChatRoot content={content} />
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      Chat unavailable
                    </div>
                  )}
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>

          {/* Mobile/Tablet Layout */}
          <div className="flex lg:hidden flex-col h-[calc(100dvh-56px)] w-full bg-background">
            {showContentViewer && (
              <div 
                className="h-[50dvh] flex-shrink-0 overflow-hidden"
                aria-hidden={!showContentViewer}
              >
                <div className="h-full w-full p-2">
                  <div className={`h-full w-full overflow-hidden rounded-xl bg-card ${
                    content && ['youtube', 'video', 'audio', 'recording'].includes(content.type) 
                      ? '' 
                      : 'border border-border'
                  }`}>
                    <div className="relative h-full w-full overflow-hidden rounded-lg bg-background">
                      {loading || authLoading ? (
                        <DocumentViewerSkeleton />
                      ) : content ? (
                        <ContentViewer content={content} />
                      ) : (
                        <div className="flex h-full items-center justify-center text-muted-foreground">
                          Content not found
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {content && (
              <ContentToggleButton
                content={content}
                isExpanded={showContentViewer}
                onToggle={() => setShowContentViewer(!showContentViewer)}
              />
            )}

            <div className="flex-1 min-h-0 overflow-hidden">
              {content ? (
                <ContentChatRoot content={content} isContentViewerVisible={showContentViewer} />
              ) : (
                <div className="flex h-full items-center justify-center text-muted-foreground">
                  Chat unavailable
                </div>
              )}
            </div>
          </div>

          <ContentShareDialog
            content={content}
            open={isShareDialogOpen}
            onOpenChange={setIsShareDialogOpen}
            onVisibilityChange={handleVisibilityChange}
          />
        </DashboardShell>
        </PublicAuthProvider>
      </ContentNavigationProvider>
    </DashboardDataProvider>
  );
}
